import type { DragConstraint } from "@project/emerge-types";
import type * as THREE from "three";
import type { CaseCornerPosition, CornerDragConstraintResult } from "./types";

/**
 * Geometry shared by the three corner drag handlers.
 *
 * Corners are placed on the *outer* shell (`±0.5 * outerDimensions` plus the
 * bracket offset), but the fields a corner drag writes are the *base* interior
 * dimensions the blueprint derives that shell from. The gap between the two —
 * panel strength, cut offsets, profile heights, rail offsets, tolerances — is
 * not knowable from the corner position alone, so every drag is resolved as a
 * movement relative to where the corner sat when the constraint was built
 * rather than as an absolute half-dimension.
 *
 * Both the rest position and the base dimension therefore have to come from the
 * same `settingsContext` snapshot. Mixing a snapshot rest with a live settings
 * base would compound across the frames that run before a rebuild refreshes the
 * context, and would break the idempotency the pointer-up re-fire relies on.
 */

/**
 * Outward direction of a corner on one axis.
 *
 * Derived from the rest position rather than the corner's name: the blueprint
 * puts `Sides.FRONT` at `+z` while the corner labels call `z < 0` "Front", so
 * the label is inverted with respect to the geometry. A corner is never at the
 * origin (its rest position always clears half the case plus the shell), so the
 * `0` case only guards against a degenerate context.
 */
export function cornerAxisSign(rest: number): number {
  return rest < 0 ? -1 : 1;
}

/**
 * Travel range of a corner along one axis, expressed relative to where the
 * corner rests, for a base dimension allowed to span `[minSize, maxSize]`.
 *
 * The case is centred on the origin, so a base dimension change of `d` moves
 * the corner by `d / 2`.
 */
export function cornerAxisBounds(
  rest: number,
  sign: number,
  base: number,
  minSize: number,
  maxSize: number,
): { lo: number; hi: number } {
  const atMin = rest + (sign * (minSize - base)) / 2;
  const atMax = rest + (sign * (maxSize - base)) / 2;
  return { lo: Math.min(atMin, atMax), hi: Math.max(atMin, atMax) };
}

/**
 * Where a corner ends up once its base dimension changed from `base` to
 * `next` — the inverse of {@link cornerAxisBounds}' half-dimension mapping.
 */
export function cornerAxisPosition(
  rest: number,
  sign: number,
  base: number,
  next: number,
): number {
  return rest + (sign * (next - base)) / 2;
}

/** The base dimensions a box-shaped interior resizes along. */
export interface CornerBaseDimensions {
  currentWidth: number;
  currentHeight: number;
  currentDepth: number;
}

/**
 * Drag constraint for interiors whose corners resize a plain width × height ×
 * depth box: bottom corners sweep width and depth across the floor plane, top
 * corners run the height up the vertical axis.
 *
 * RACK and CASE differ only in where their base dimensions come from and in the
 * `interiorType` tag they carry, so they share this whole construction. RACK19
 * builds its own: its width is fixed at 19", and its height snaps to rack units.
 */
export function buildBoxCornerConstraint(
  caseCornerPosition: CaseCornerPosition,
  position: THREE.Vector3,
  interiorType: string,
  dimensions: CornerBaseDimensions,
  minSize: number,
  maxSize: number,
): CornerDragConstraintResult {
  const { currentWidth, currentHeight, currentDepth } = dimensions;
  const isBottom = caseCornerPosition.endsWith("Bottom");

  if (isBottom) {
    const xSign = cornerAxisSign(position.x);
    const zSign = cornerAxisSign(position.z);
    const xRange = cornerAxisBounds(
      position.x,
      xSign,
      currentWidth,
      minSize,
      maxSize,
    );
    const zRange = cornerAxisBounds(
      position.z,
      zSign,
      currentDepth,
      minSize,
      maxSize,
    );

    const dragConstraint: DragConstraint = {
      type: "surface",
      planeNormal: [0, 1, 0],
      bounds: {
        min: [xRange.lo, position.y, zRange.lo],
        max: [xRange.hi, position.y, zRange.hi],
      },
      snapAnchors: [],
      sourceOptionUuid: "interior_corner_resize_xz",
    };

    return {
      dragConstraint,
      settingsContext: {
        caseCornerPosition,
        mode: "xz",
        interiorType,
        xSign,
        zSign,
        restX: position.x,
        restZ: position.z,
        ...dimensions,
      },
    };
  }

  const yRange = cornerAxisBounds(
    position.y,
    1,
    currentHeight,
    minSize,
    maxSize,
  );

  const dragConstraint: DragConstraint = {
    type: "edge",
    axis: [0, 1, 0],
    bounds: {
      min: [position.x, yRange.lo, position.z],
      max: [position.x, yRange.hi, position.z],
    },
    snapAnchors: [],
    sourceOptionUuid: "interior_corner_resize_y",
  };

  return {
    dragConstraint,
    settingsContext: {
      caseCornerPosition,
      mode: "y",
      interiorType,
      restY: position.y,
      ...dimensions,
    },
  };
}
